import {
	SchemaMessageVariant,
	getMarkdownDescription,
	getSchemaMessages,
	usesMarkdownDescription,
	usesFullWidth
} from "./schemaUiMessages";

export interface JsonSchemaDefinition {
	type?: string | string[];
	title?: unknown;
	description?: unknown;
	properties?: Record<string, JsonSchemaDefinition>;
	items?: JsonSchemaDefinition;
	required?: string[];
	enum?: unknown[];
	minLength?: number;
	maxLength?: number;
	pattern?: string;
	allOf?: JsonSchemaDefinition[];
	anyOf?: JsonSchemaDefinition[];
	not?: JsonSchemaDefinition;
	metadata?: Record<string, unknown>;
	[key: string]: unknown;
}

interface SchemaFormLayout {
	htmlClass: string;
	condition?: string;
	notitle?: boolean;
	description?: string;
}

export interface SchemaFormControlDefinition extends SchemaFormLayout {
	key: string[];
	items?: SchemaFormDefinition[];
	type?: string;
}

export interface SchemaFormMessageDefinition extends SchemaFormLayout {
	type: "markdown";
	markdown: string;
	variant?: SchemaMessageVariant;
	key?: never;
	items?: never;
}

export interface SchemaFormSectionDefinition extends SchemaFormLayout {
	type: "section";
	items: SchemaFormDefinition[];
	key?: never;
}

export type SchemaFormDefinition = SchemaFormControlDefinition
	| SchemaFormMessageDefinition | SchemaFormSectionDefinition;

interface SchemaValueCondition {
	property: string;
	values: unknown[];
}

interface ConditionalPropertyRule {
	property: string;
	conditions: SchemaValueCondition[];
}

export function isSchemaPropertyVisible(
	schema: JsonSchemaDefinition,
	property: string,
	model: Record<string, unknown> = {}
): boolean {
	const rule = getConditionalPropertyRules(schema)
		.find(candidate => candidate.property === property);

	return !rule || rule.conditions.every(condition => condition.values.includes(model[condition.property]));
}

export function getSchemaFormCondition(
	schema: JsonSchemaDefinition,
	property: string,
	modelPath: string[] = []
): string | undefined {
	const rule = getConditionalPropertyRules(schema)
		.find(candidate => candidate.property === property);

	if(!rule) {
		return undefined;
	}

	return rule.conditions
		.map(condition => {
			const modelExpression = modelPath.concat(condition.property)
				.reduce((expression, part) => `${expression}[${JSON.stringify(part)}]`, "model");
			const valueExpressions = condition.values
				.map(value => `${modelExpression} === ${JSON.stringify(value)}`);

			return valueExpressions.length === 1
				? valueExpressions[0]
				: `(${valueExpressions.join(" || ")})`;
		})
		.join(" && ");
}

export function buildSchemaFormSchema(schema: JsonSchemaDefinition): JsonSchemaDefinition {
	return cloneSchemaForForm(schema, true);
}

export function buildSchemaForm(
	schema: JsonSchemaDefinition,
	modelPath: string[] = []
): SchemaFormDefinition[] {
	const messages = getSchemaMessages(schema);
	const definitions: SchemaFormDefinition[] = [];
	const description = getMarkdownDescription(schema);
	if(description) {
		definitions.push(markdownDefinition(description));
	}
	definitions.push(...messages.filter(message => message.before === undefined && message.after === undefined)
		.map(message => markdownDefinition(message.markdown, message.variant)));
	for(const [key, propertySchema] of Object.entries(schema.properties ?? {})) {
		definitions.push(...messages.filter(message => message.before === key)
			.map(message => markdownDefinition(message.markdown, message.variant)));
		const keyPath = modelPath.concat(key);
		const isObject = hasSchemaType(propertySchema, "object");
		const isArray = hasSchemaType(propertySchema, "array");
		const isBoolean = hasSchemaType(propertySchema, "boolean");
		const definition: SchemaFormControlDefinition = {
			key: keyPath,
			htmlClass: isObject
				? "schema-form-object-card"
				: isArray
					? "schema-form-array-field"
					: usesFullWidth(propertySchema)
						? "col-xs-12 schema-form-responsive-column"
						: isBoolean
							? "schema-form-column-small"
							: "col-sm-6 col-md-4 schema-form-responsive-column"
		};
		const condition = getSchemaFormCondition(schema, key, modelPath);

		if(condition) {
			definition.condition = condition;
		}
		if(isObject) {
			definition.items = buildSchemaForm(propertySchema, keyPath);
		}
		if(usesMarkdownDescription(propertySchema)) {
			// Prevent the standard decorator from also displaying the unparsed description.
			definition.description = "";
		}
		const propertyDescription = getMarkdownDescription(propertySchema);
		if(!isObject && propertyDescription) {
			const wrapper: SchemaFormSectionDefinition = {
				type: "section",
				htmlClass: definition.htmlClass,
				items: [definition, {
					...markdownDefinition(propertyDescription),
					htmlClass: "schema-form-markdown-description"
				}]
			};
			if(condition) {
				wrapper.condition = condition;
				delete definition.condition;
			}
			definition.htmlClass = "";
			definitions.push(wrapper);
		} else {
			definitions.push(definition);
		}
		definitions.push(...messages.filter(message => message.after === key)
			.map(message => markdownDefinition(message.markdown, message.variant)));
	}
	return definitions;
}

function markdownDefinition(markdown: string, variant?: SchemaMessageVariant): SchemaFormMessageDefinition {
	return { type: "markdown", markdown, variant, htmlClass: "schema-form-markdown-message" };
}

function cloneSchemaForForm(
	schema: JsonSchemaDefinition,
	isPresent: boolean
): JsonSchemaDefinition {
	if(!schema || typeof schema !== "object") {
		return schema;
	}

	const formSchema = { ...schema };

	if(hasSchemaType(schema, "object") && schema.properties) {
		const requiredProperties = new Set(isPresent ? schema.required ?? [] : []);
		const conditionallyRequiredProperties = new Set(
			getConditionalPropertyRules(schema).map(rule => rule.property)
		);
		formSchema.properties = Object.entries(schema.properties).reduce((properties, [key, propertySchema]) => {
			properties[key] = cloneSchemaForForm(
				propertySchema,
				requiredProperties.has(key) || conditionallyRequiredProperties.has(key)
			);
			return properties;
		}, {});

		if(!isPresent) {
			delete formSchema.required;
		}
	}

	if(hasSchemaType(schema, "array") && schema.items) {
		formSchema.items = cloneSchemaForForm(schema.items, true);
	}

	return formSchema;
}

export function hasSchemaType(
	schema: JsonSchemaDefinition | null | undefined,
	type: string
): boolean {
	const schemaTypes = Array.isArray(schema?.type) ? schema.type : [schema?.type];
	const normalizedType = type.toLowerCase();

	return schemaTypes.some(schemaType => typeof schemaType === "string"
		&& schemaType.toLowerCase() === normalizedType);
}

export function normalizeSchemaFormModel(
	model: unknown,
	schema?: JsonSchemaDefinition
): unknown {
	if(isEmptyNumericValue(model, schema)) {
		return hasSchemaType(schema, "null") ? null : undefined;
	}

	if(Array.isArray(model) && schema?.items) {
		return model
			.map(item => normalizeSchemaFormModel(item, schema.items))
			.filter(item => item !== undefined);
	}

	if(model && typeof model === "object" && schema?.properties) {
		return Object.entries(model).reduce((normalizedModel, [key, value]) => {
			const propertySchema = schema.properties?.[key];
			const normalizedValue = propertySchema
				? normalizeSchemaFormModel(value, propertySchema)
				: value;

			if(normalizedValue !== undefined) {
				normalizedModel[key] = normalizedValue;
			}

			return normalizedModel;
		}, {} as Record<string, unknown>);
	}

	return model;
}

function isEmptyNumericValue(
	value: unknown,
	schema?: JsonSchemaDefinition
): boolean {
	const isNumeric = ["number", "integer", "datapoint", "entity"]
		.some(type => hasSchemaType(schema, type));
	const isEmpty = value === null
		|| value === undefined
		|| (typeof value === "string" && value.trim() === "");

	return isNumeric && isEmpty;
}

function getConditionalPropertyRules(schema: JsonSchemaDefinition): ConditionalPropertyRule[] {
	if(schema.anyOf?.length !== 2) {
		return [];
	}

	const negatedBranch = schema.anyOf.find(branch => branch.not);
	const requiredBranch = schema.anyOf.find(branch => branch !== negatedBranch && branch.required?.length);
	const conditions = negatedBranch?.not ? getValueConditions(negatedBranch.not) : [];

	if(!requiredBranch?.required?.length || conditions.length === 0) {
		return [];
	}

	return requiredBranch.required.map(property => ({ property, conditions }));
}

function getValueConditions(schema: JsonSchemaDefinition): SchemaValueCondition[] {
	if(!schema.required?.length || !schema.properties) {
		return [];
	}

	const conditions = schema.required.map(property => ({
		property,
		values: schema.properties?.[property]?.enum ?? []
	}));

	return conditions.every(condition => condition.values.length > 0) ? conditions : [];
}
